import type { Json } from '@/types/database';
import { supabase } from '@/lib/supabase';

export interface PollResultOption {
  optionId: string;
  label: string;
  sortIndex: number;
  playerCount: number;
  totalCount: number;
}

export interface PollResponseResult {
  alreadyAnswered: boolean;
  xpAwarded: number;
  distribution: PollResultOption[];
}

export interface PollResponseSummary {
  selectedOptionId: string | null;
  distribution: PollResultOption[];
  optionId?: string;
  pollDefinitionId?: string;
  sessionId?: string | null;
  persistenceRevision?: number;
  xpEarned?: number;
}

type PollResultRpcRow = {
  option_id: string;
  label: string;
  sort_index: number;
  player_count: number;
};

type PollResponseRpcResult = {
  already_answered?: boolean;
  xp_awarded?: number;
  distribution?: unknown;
};

type PollResponseSummaryRpcResult = {
  selected_option_id?: string | null;
  option_id?: string | null;
  poll_definition_id?: string | null;
  session_id?: string | null;
  persistence_revision?: number | null;
  xp_earned?: number | null;
  distribution?: unknown;
};

function mapPollResultRow(row: PollResultRpcRow): PollResultOption {
  return {
    optionId: row.option_id,
    label: row.label,
    sortIndex: row.sort_index,
    playerCount: row.player_count,
    totalCount: row.player_count,
  };
}

function mapDistribution(value: unknown): PollResultOption[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }

    const record = entry as Record<string, unknown>;
    if (typeof record.option_id !== 'string' || typeof record.label !== 'string') {
      return [];
    }

    return [
      {
        optionId: record.option_id,
        label: record.label,
        sortIndex: typeof record.sort_index === 'number' ? record.sort_index : 0,
        playerCount: typeof record.player_count === 'number' ? record.player_count : 0,
        totalCount: typeof record.player_count === 'number' ? record.player_count : 0,
      },
    ];
  });
}

export function mapPollResponseResult(value: Json | null): PollResponseResult {
  const record = (value && typeof value === 'object' ? value : {}) as PollResponseRpcResult;

  return {
    alreadyAnswered: record.already_answered === true,
    xpAwarded: typeof record.xp_awarded === 'number' ? record.xp_awarded : 0,
    distribution: mapDistribution(record.distribution),
  };
}

export function mapPollResponseSummary(value: Json | null): PollResponseSummary {
  const record = (value && typeof value === 'object' ? value : {}) as PollResponseSummaryRpcResult;

  const selectedOptionId =
    typeof record.selected_option_id === 'string'
      ? record.selected_option_id
      : typeof record.option_id === 'string'
        ? record.option_id
        : null;
  const summary: PollResponseSummary = {
    selectedOptionId,
    distribution: mapDistribution(record.distribution),
  };
  if (typeof record.option_id === 'string') summary.optionId = record.option_id;
  if (typeof record.poll_definition_id === 'string') {
    summary.pollDefinitionId = record.poll_definition_id;
  }
  if (record.session_id === null || typeof record.session_id === 'string') {
    summary.sessionId = record.session_id;
  }
  if (typeof record.persistence_revision === 'number') {
    summary.persistenceRevision = record.persistence_revision;
  }
  if (typeof record.xp_earned === 'number') summary.xpEarned = record.xp_earned;
  return summary;
}

export async function getPollResults(input: {
  pollDefinitionId: string;
}): Promise<PollResultOption[]> {
  const { data, error } = await supabase.rpc('get_poll_results', {
    p_poll_definition_id: input.pollDefinitionId,
  });

  if (error) {
    throw error;
  }

  return ((data as PollResultRpcRow[] | null) ?? []).map(mapPollResultRow);
}
